import { connectUtil, PropsFromRedux } from "base/src/utils/reduxUtil";
import { RootStateCampanha } from "../../../../store/campanhaReducer";
import { LotePedidoResponse } from "../../../../types/lotePedido/lotePedidoResponse";
import { useCarteira } from "domain/utils/context/carteiraBaseContext";
import { formatToBRL } from "base/src/utils/mathUtil";
import { Button } from "base/src/components/Button";
import * as S from './ModalRealizarPagamentoStyles';
import { PagarPedidoRequest } from "../../../../types/pagarPedido/pagarPedidoRequest";
import { CampanhaPedidoResgateResponse } from "../../../../types/campanha/campanhaPedidoResgateResponse";
import { useState, useEffect } from "react";
import { ObjectControl } from "domain/utils/ReactUtils";
import PagedLoteItemDetalhe from "../PagedLoteItemDetalhe/PagedLoteItemDetalhe";
import { PagarLote, TentarNovamente } from "../../../../store/pedidoLotes/actions/pedidoLotesAction";
import Modal2FA from "../Modal2FA/Modal2FA";
import { _UserData, UserDataBaseEnum } from "base/src/infra/storage/localStorage";

const connector = connectUtil(
  (state: RootStateCampanha) => ({
    CarteiraUsuarioInfo: state.CarteiraBaseReducer.CarteiraUsuario,
    loadingStates: state.BaseReducer.Loading
  }),
  { PagarLote, TentarNovamente }
);

export interface ModalRealizarPagamentoProps extends PropsFromRedux<typeof connector> {
  loteItem: LotePedidoResponse;
  campanhaId: number;
  onCancel?: () => void;
  onConfirm?: () => void;
  onModalClose?: () => void;
}

function ModalRealizarPagamento(props: ModalRealizarPagamentoProps) {
  const { walletBalance } = useCarteira();
  const saldoFinal = (walletBalance || 0) - (Number(props.loteItem.ValorTotal) || 0);
  const temSaldoSuficiente = saldoFinal >= 0;
  const isPagamentoComErro = props.loteItem.CampanhaPedidoResgatePagamentoStatusID === 5;
  const isPagamentoJaRealizado = props.loteItem.CampanhaPedidoResgatePagamentoStatusID === 3;
  const podeRealizarPagamento = temSaldoSuficiente && !isPagamentoJaRealizado;

  const initialState = {} as CampanhaPedidoResgateResponse
  const [request, setRequest] = useState<CampanhaPedidoResgateResponse>(initialState as CampanhaPedidoResgateResponse);
  const control = new ObjectControl(request, setRequest);

  // Add reload trigger for PagedLoteItemDetalhe
  const [reloadTrigger, setReloadTrigger] = useState(0);

  // Estados para controlar o fluxo após pagamento
  // O modal permanecerá aberto após pagamentos para permitir visualização dos resultados
  const [operacaoExecutada, setOperacaoExecutada] = useState(false);
  const [loadingAnterior, setLoadingAnterior] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Estados para controle do 2FA
  // const [show2FAModal, setShow2FAModal] = useState(false);
  // const [pendingPaymentRequest, setPendingPaymentRequest] = useState<PagarPedidoRequest | null>(null);
  // const [isRetryPayment, setIsRetryPayment] = useState(false);

  // Monitora o estado de loading do pagamento
  const isLoading = props.loadingStates?.some(loading => loading.Name === 'PAGARPEDIDO') || false;

  useEffect(() => {
    // Detecta quando uma operação termina (loading era true e agora é false)
    if (loadingAnterior && !isLoading && operacaoExecutada) {
      // Recarrega o conteúdo do modal
      setReloadTrigger(prev => prev + 1);
      setOperacaoExecutada(false);

      // Mostra mensagem de sucesso temporariamente
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      // Dispara evento para recarregar a lista principal, mas não fecha o modal
      window.dispatchEvent(new CustomEvent('reloadLoteList'));
    }
    setLoadingAnterior(isLoading);
  }, [isLoading, operacaoExecutada, loadingAnterior]);

  function handleCancel() {
    // Fecha o modal apenas quando o usuário clica explicitamente em "Cancelar"
    props.onCancel?.();
    props.onModalClose?.();
  }

  function handlePagamento() {
    if (podeRealizarPagamento) {
      const request: PagarPedidoRequest = {
        CampanhaPedidoResgatePagamentoID: props.loteItem.CampanhaPedidoResgatePagamentoID,
        CarteiraID: props.CarteiraUsuarioInfo[0].CarteiraID,
        CodigoZap: '' // 2FA desativado temporariamente
      };

      setOperacaoExecutada(true);

      // Executa pagamento direto sem 2FA
      if (isPagamentoComErro) {
        props.TentarNovamente(request);
      } else {
        props.PagarLote(request);
      }

      // // === 2FA FLOW (COMMENTED OUT) ===
      // // Guarda a requisição e o tipo de operação
      // setPendingPaymentRequest(baseRequest);
      // setIsRetryPayment(isPagamentoComErro);
      // // Abre modal 2FA
      // setShow2FAModal(true);
    }
  }

  // function handle2FAConfirm(codigoVerificacao: string) {
  //   if (pendingPaymentRequest) {
  //     const request: PagarPedidoRequest = {
  //       ...pendingPaymentRequest,
  //       CodigoZap: codigoVerificacao
  //     };

  //     setOperacaoExecutada(true);
  //     setShow2FAModal(false);

  //     if (isRetryPayment) {
  //       props.TentarNovamente(request);
  //     } else {
  //       props.PagarLote(request);
  //     }

  //     // Limpa estados do 2FA
  //     setPendingPaymentRequest(null);
  //     setIsRetryPayment(false);
  //   }
  // }

  // function handle2FAClose() {
  //   setShow2FAModal(false);
  //   setPendingPaymentRequest(null);
  //   setIsRetryPayment(false);
  // }

  return (
    <S.Container>
      <S.ScrollableContent>
        <PagedLoteItemDetalhe
          control={control}
          loteDetail={props.loteItem}
          take={3}
          loteId={props.loteItem.CampanhaPedidoResgatePagamentoID}
          reloadTrigger={reloadTrigger}
          campanhaId={props.campanhaId}
        />

        {showSuccessMessage && (
          <div style={{
            padding: '12px',
            backgroundColor: '#d4edda',
            borderRadius: '6px',
            color: '#155724',
            fontSize: '14px',
            textAlign: 'center',
            marginBottom: '16px',
            border: '1px solid #c3e6cb'
          }}>
            ✅ Operação realizada com sucesso! Os dados do lote foram atualizados automaticamente.
          </div>
        )}

        {isPagamentoComErro && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fff3cd',
            borderRadius: '6px',
            color: '#856404',
            fontSize: '14px',
            textAlign: 'center',
            marginBottom: '16px',
            border: '1px solid #ffeaa7'
          }}>
            ⚠️ Alguns pagamentos no lote apresentaram falha. Você pode tentar processar novamente os itens pendentes.
          </div>
        )}

        <div>
          <S.InfoRow>
            <S.Label>Saldo disponível:</S.Label>
            <S.Value style={{ color: '#28a745' }}>{formatToBRL(walletBalance || 0)}</S.Value>
          </S.InfoRow>

          <S.InfoRow>
            <S.Label>Valor do lote:</S.Label>
            <S.Value style={{ color: '#dc3545' }}>{formatToBRL(props.loteItem.ValorTotal || 0)}</S.Value>
          </S.InfoRow>

          <S.InfoRow>
            <S.Label>Valor taxa:</S.Label>
            <S.Value style={{ color: '#dc3545' }}>{formatToBRL(props.loteItem.ValorTaxa || 0)}</S.Value>
          </S.InfoRow>

          <S.InfoRow>
            <S.Label>Saldo final:</S.Label>
            <S.Value style={{ color: '#007bff' }}>
              {formatToBRL(saldoFinal)}
            </S.Value>
          </S.InfoRow>
        </div>
      </S.ScrollableContent>

      <S.FixedFooter>
        {!temSaldoSuficiente && (
          <div style={{
            padding: '12px',
            backgroundColor: '#fca7a7',
            borderRadius: '6px',
            color: '#721c24',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            Saldo insuficiente para realizar o pagamento
          </div>
        )}

        {isPagamentoJaRealizado && (
          <div style={{
            padding: '12px',
            backgroundColor: '#d1ecf1',
            borderRadius: '6px',
            color: '#0c5460',
            fontSize: '14px',
            textAlign: 'center'
          }}>
            ✅ Este lote já foi pago com sucesso e não pode ser processado novamente
          </div>
        )}

        <S.ButtonContainer>
          <Button
            size="md"
            variant="danger"
            full
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          <Button
            size="md"
            variant="success"
            full
            disabled={!podeRealizarPagamento || isLoading}
            onClick={handlePagamento}
          >
            {isLoading
              ? "⏳ Processando..."
              : isPagamentoJaRealizado
                ? "✅ Pagamento Já Realizado"
                : isPagamentoComErro
                  ? "🔄 Tentar Pagamento Novamente"
                  : "Realizar Pagamento"
            }
          </Button>
        </S.ButtonContainer>
      </S.FixedFooter>

      {/* Modal 2FA - TEMPORARILY DISABLED */}
      {/* <Modal2FA
        isOpen={show2FAModal}
        onClose={handle2FAClose}
        onConfirm={handle2FAConfirm}
        userPhone={_UserData.Get(UserDataBaseEnum.TelefoneCelular)}
        usuarioID={_UserData.Get(UserDataBaseEnum.UsuarioID)}
        sistemaID={1}
        isLoading={isLoading}
      /> */}
    </S.Container>
  );
}

export default connector(ModalRealizarPagamento);